// Local shim for `@s-ui/react-router`.
//
// Aliased in `vite.config.js` via `resolve.alias`, so every `import ... from
// '@s-ui/react-router'` in the app and in `packages/ui` is routed here; this file loads the
// real compatibility shim (`@adv-mt/shim-react-router`) directly, which the alias leaves
// alone.
//
// The reason for the shim: `push`/`replace` are the only way `packages/ui` navigates, and the
// `state` they carry is what the destination's RR7 loader cannot see (see
// `navigationStateCache.ts`). Recording it here covers every caller at once instead of
// leaving each one to remember, which is how the listing→detail Segment attribution went
// missing in the first place.
import {useContext, useMemo} from 'react'

import Context from '@s-ui/react-context'
import {
  Link,
  NavLink,
  RouterContext,
  useLocation,
  useParams,
  useRouter as baseUseRouter
} from '@adv-mt/shim-react-router'

import {getLegacyRoutes} from './legacyRoutes.js'
import {setNavigationState} from './navigationStateCache.ts'

export {Link, NavLink, RouterContext, useLocation, useParams}

const recordNavigationState = navigate => target => {
  if (typeof target === 'object' && target !== null) {
    setNavigationState(target.pathname ?? '', target.state)
  }

  return navigate(target)
}

// `routes` is the matched-route list of the legacy router, which `@s-ui/react-web-vitals` needs to tag
// (and allow) each metric and the ad detail rewrites for its variant. It comes from the legacy route
// table, not from RR7, so routing and loaders are untouched.
export const useRouter = () => {
  const router = baseUseRouter()
  const {isMotos} = useContext(Context) ?? {}
  const {pathname} = router.location

  return useMemo(
    () => ({
      ...router,
      routes: getLegacyRoutes({pathname, isMotos: Boolean(isMotos)}),
      push: recordNavigationState(router.push),
      replace: recordNavigationState(router.replace)
    }),
    [router, pathname, isMotos]
  )
}
